import { Button, makeStyles, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import './ModelSelection.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ClientConfiguration, IConversationPatch } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { updateConversationAction } from '../../store/actions/conversationActions';
import { useConversations } from '../../store/hooks';
import SyButton from '../Common/SyButton';

const ChevronDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

class ModelSelectionProps {
    changeModel: (model: string) => void = () => {};
    disabled: boolean = false;
}

const useClasses = makeStyles({
    modelSelection: {
        width: 'auto',
    },
});

export default function ModelSelection(props: ModelSelectionProps) {
    const { changeModel, disabled } = props;
    const [clientConfiguration, setClientConfiguration] = useState<ClientConfiguration>();
    const [menuTitle, setMenuTitle] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const classes = useClasses();
    const { activeChat, dispatch } = useConversations();
    const { activeGpt } = useConversations();
    const userSelectedModelasRef = useRef<string>("");

    const handleModelChange = useCallback((model: string) => {
        setMenuTitle(model);
        changeModel(model);        
        userSelectedModelasRef.current = model; // Mark that user has manually selected a model
        
        // Update the active chat with the selected model using the proper action dispatch
        if (activeChat?.id) {
            dispatch(updateConversationAction({
                id: activeChat.id,
                patch: { llmDeploymentName: model, llm: model } as IConversationPatch & { llm: string }
            }));
        }
    }, [changeModel, activeChat?.id, dispatch]);

    useEnterpriseGPTApiCall(async (client, abortSignal) => {
        try {
            setHasError(false);
            const getClientConfigurationsOptions = await client.getClientConfigurationsOptions(abortSignal);
            setClientConfiguration(getClientConfigurationsOptions);

            // Only auto-set model if user hasn't manually selected one or if the conversation is already started
            if (!userSelectedModelasRef.current || (activeChat?.prompts && activeChat.prompts.length > 0)) {
                if (activeChat?.prompts && activeChat.prompts.length > 0
                    && activeChat?.llm
                    && getClientConfigurationsOptions?.llmModels?.includes(activeChat.llm)) {
                    setMenuTitle(activeChat.llm);
                    changeModel(activeChat.llm);
                } else if (getClientConfigurationsOptions?.llmModels && getClientConfigurationsOptions.llmModels.length > 0) {
                    setMenuTitle(getClientConfigurationsOptions.llmModels[0]);
                    changeModel(getClientConfigurationsOptions.llmModels[0]);
                }
            }
            setIsLoading(false);
        } catch (e: unknown) {
            if ((e as Error).message !== 'canceled') {
                console.error('Error fetching client configurations options:', e);
                setHasError(true);
                // Create a fallback configuration to maintain UI consistency
                const fallbackConfig = new ClientConfiguration();
                fallbackConfig.llmModels = ['Default Model'];
                setClientConfiguration(fallbackConfig);
                setMenuTitle('Model Selection');
            }
            setIsLoading(false);
        }
    }, [changeModel]);

    useEffect(() => {
        // Only restore the model from activeChat when switching to a different chat
        // and that chat has a model already set, and user hasn't manually selected a model
        if ( activeChat && activeChat.llm && activeChat.llm != '') {
            setMenuTitle(activeChat.llm);
            changeModel(activeChat.llm);            
        } else if (userSelectedModelasRef.current != "") {
            // Reset when no active chat
            setMenuTitle(userSelectedModelasRef.current);
            changeModel(userSelectedModelasRef.current);
        }
    }, [activeChat, changeModel, dispatch]);

    // Don't render if there are less than 2 models available (and no error state)
    if (!hasError && clientConfiguration?.llmModels && clientConfiguration.llmModels.length < 2) {
        return null;
    }

    return (
        <>
            <Menu positioning='below-start'>
                <MenuTrigger disableButtonEnhancement>
                    <SyButton appearance='outline' disabled={disabled || hasError || isLoading} icon={<ChevronDownIcon />} iconPosition="after" className="sy-model-trigger"><span className="sy-model-trigger-label">{menuTitle}</span></SyButton>
                </MenuTrigger>
                <MenuPopover className="sy-model-menu-popover">
                    <MenuList className="sy-model-menu-list">
                        {clientConfiguration?.llmModels?.map((model) => (
                            <MenuItem className={`sy-model-menu-item ${menuTitle === model ? 'sy-selected' : ''}`} key={model} onClick={() => handleModelChange(model)} aria-checked={menuTitle === model}>
                                <span className="sy-model-item-label">{model}</span>
                                {menuTitle === model && <span className="sy-model-item-check" aria-hidden><CheckIcon/></span>}
                            </MenuItem>
                        ))}
                    </MenuList>
                </MenuPopover>
            </Menu>
        </>
    );
}
